import React, { useState, useEffect, useLayoutEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Package, Share2 } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { handleSquareError } from '../../secret/lib/square'
import { EnterpriseToast } from '../../secret/components/ui/EnterpriseToast'
import { toast } from 'sonner'

import ReservationTimer from '../../secret/components/checkout/ReservationTimer'
import TastePaymentForm from '../components/TastePaymentForm'
import PackSelector from '../components/PackSelector'
import CaviarUpsell from '../components/CaviarUpsell'
import OrderSummary from '../components/OrderSummary'
import TasteCountdown from '../components/TasteCountdown'
import FreezerToTable from '../components/FreezerToTable'
import ShippingExpectationNote from '../components/ShippingExpectationNote'
import ShippingEstimator from '../components/ShippingEstimator'
import TasteFaq from '../components/TasteFaq'
import WaitlistForm from '../components/WaitlistForm'
import Footer from '../../components/Footer'

import { useTaste } from '../contexts/TasteContext'
import { useTastePayment } from '../contexts/TastePaymentContext'
import useAsset from '../../hooks/useAsset'
import { CAMPAIGN } from '../config/campaign'

// US States for shipping address
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
]

const TastePage: React.FC = () => {
  const navigate = useNavigate()
  const {
    selectedPack,
    hasCaviar,
    caviarPrice,
    quote,
    fetchQuote,
    quoteLoading,
    totalAmount,
    customerEmail,
    setCustomerEmail,
    customerName,
    setCustomerName,
    shippingAddress,
    setShippingAddress,
    reservation,
    createReservation,
    campaignSoldOut,
    campaignClosed,
  } = useTaste()

  const campaignOver = campaignSoldOut || campaignClosed

  const { initializePayment, processCardPayment, processApplePayment, processGooglePayment, setError: setPaymentError } = useTastePayment()

  // Cache-busted asset URLs
  const logoWhite = useAsset('/tailtations-white.svg')
  const repeatBg = useAsset('/repeat-bg.png')
  const heroImage = useAsset('/images/taste/04-hero-gold-flake.jpg')
  const prepImage = useAsset('/images/taste/01-prep-batter-dip.jpg')
  const cookImage = useAsset('/images/taste/02-cook-fryer-action.jpg')
  const preserveImage = useAsset('/images/taste/03-preserve-blast-chiller.jpg')
  const offerImage = useAsset('/images/taste/05-packaging-caviar-offer.jpg')

  const [step, setStep] = useState<'select' | 'details' | 'payment' | 'success'>('select')
  const [orderId, setOrderId] = useState<string | null>(null)
  const [orderNumber, setOrderNumber] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isCreatingReservation, setIsCreatingReservation] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [addressErrors, setAddressErrors] = useState<Record<string, string>>({})

  // Force dark mode on <html> so :root:not(.dark) nuclear CSS rules don't fire
  // The /taste page has a dark background but the site-wide theme may be 'light'
  useLayoutEffect(() => {
    const root = document.documentElement
    const wasDark = root.classList.contains('dark')
    root.classList.add('dark')
    return () => {
      if (!wasDark) {
        root.classList.remove('dark')
      }
    }
  }, [])


  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [step])

  // Hard-stop checkout if the campaign sells out mid-flow. A completed order
  // ('success') is left alone; otherwise bounce back to the sold-out landing.
  // The server-side cap in convert-reservation-to-taste-order is the final
  // authority — this just keeps the UI from walking through a dead path.
  useEffect(() => {
    if (campaignOver && step !== 'select' && step !== 'success') {
      setStep('select')
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title={campaignClosed ? 'Pre-Order Closed' : 'Sold Out'}
          message={campaignClosed
            ? 'The pre-order window closed before your order was placed. You have not been charged.'
            : 'This batch sold out before your order was placed. You have not been charged.'}
          type="error"
        />
      ))
    }
  }, [campaignOver, step])

  // Refresh the live shipping + tax quote whenever the pack, caviar choice, or
  // destination (state / ZIP) changes while on the details step.
  useEffect(() => {
    if (step !== 'details') return
    fetchQuote()
  }, [step, selectedPack?.size, hasCaviar, shippingAddress.state, shippingAddress.zip, fetchQuote])

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // This campaign ships to the contiguous 48 states only. Cold-chain
  // 2-Day air to AK/HI is not reliably 2 transit days, and PR/VI add customs
  // complexity — so those destinations are rejected at checkout. We detect them
  // by state (AK/HI) and by ZIP prefix (AK 995–999, HI 967–968, plus PR/VI/
  // territory prefixes 006–009 which aren't in the state dropdown at all).
  const SHIP_RESTRICTED_MESSAGE =
    "We're sorry — this campaign ships to the contiguous 48 states only. Stay tuned for future drops!"

  const isShipRestricted = (state: string, zip: string): boolean => {
    if (state === 'AK' || state === 'HI') return true
    const prefix = zip.trim().slice(0, 3)
    if (/^99[5-9]$/.test(prefix)) return true // Alaska
    if (prefix === '967' || prefix === '968') return true // Hawaii
    if (/^00[6-9]$/.test(prefix)) return true // PR / VI / territories
    return false
  }

  // Validate shipping address
  const validateAddress = (): boolean => {
    const errors: Record<string, string> = {}
    if (!shippingAddress.street.trim()) errors.street = 'Street address is required'
    if (!shippingAddress.city.trim()) errors.city = 'City is required'
    if (!shippingAddress.state) errors.state = 'State is required'
    if (!shippingAddress.zip.trim()) errors.zip = 'ZIP code is required'
    else if (!/^\d{5}$/.test(shippingAddress.zip.trim())) errors.zip = 'ZIP must be 5 digits'
    else if (isShipRestricted(shippingAddress.state, shippingAddress.zip)) {
      errors.zip = SHIP_RESTRICTED_MESSAGE
    }
    setAddressErrors(errors)
    return Object.keys(errors).length === 0
  }


  // Handle continue to details
  const handleContinueToDetails = () => {
    if (campaignOver) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title={campaignClosed ? 'Pre-Order Closed' : 'Sold Out'}
          message={campaignClosed ? 'The pre-order window has ended. No further orders can be placed.' : 'This batch is sold out. No further orders can be placed.'}
          type="error"
        />
      ))
      return
    }
    if (!selectedPack) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title="Pack Required"
          message="Please select a pack size"
          type="error"
        />
      ))
      return
    }
    setStep('details')
  }

  // Handle continue to payment
  const handleContinueToPayment = async () => {
    if (!customerEmail) {
      setEmailError('Email is required for your order confirmation')
      document.getElementById('email-input-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    if (!validateEmail(customerEmail)) {
      setEmailError('Please enter a valid email address')
      document.getElementById('email-input-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    if (!customerName.trim()) {
      setEmailError('Full name is required')
      return
    }

    if (!validateAddress()) {
      return
    }

    setEmailError(null)
    setIsCreatingReservation(true)

    try {
      await createReservation(totalAmount)
      setStep('payment')
    } catch (error: any) {
      console.error('Error creating reservation:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title="Reservation Failed"
          message={error.message || 'Failed to create reservation. Please try again.'}
          type="error"
        />
      ))
    } finally {
      setIsCreatingReservation(false)
    }
  }

  // Initialize payment form when entering payment step
  useEffect(() => {
    if (step === 'payment' && reservation) {
      const timeoutId = setTimeout(() => {
        initializePayment('taste-card-container', Math.round(totalAmount * 100))
          .catch(error => {
            console.error('Payment initialization failed:', error)
            toast.custom((t) => (
              <EnterpriseToast
                t={t}
                title="Payment Form Error"
                message="Failed to initialize payment form. Please try again."
                type="error"
              />
            ))
          })
      }, 100)

      return () => clearTimeout(timeoutId)
    }
  }, [step, reservation, totalAmount, initializePayment])

  // Handle reservation expiration
  const handleReservationExpire = () => {
    toast.custom((t) => (
      <EnterpriseToast
        t={t}
        title="Reservation Expired"
        message="Your reservation has expired. Please try again."
        type="error"
      />
    ))
    setStep('select')
  }

  // Build order description for Square
  const getOrderDescription = () => {
    let desc = `Tailtations ${selectedPack?.size}-Pack Pre-Order`
    if (hasCaviar) desc += ' + Caviar'
    return desc
  }

  // Process payment
  const handleProcessPayment = async (paymentMethod: 'card' | 'apple' | 'google') => {
    if (!reservation) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title="No Reservation"
          message="No reservation found. Please try again."
          type="error"
        />
      ))
      return
    }

    setIsProcessing(true)

    try {
      // Tokenize the payment method FIRST — for ALL methods — using the
      // reservation id as the reference. Tokenization can fail (declined card,
      // user cancels the wallet sheet); doing it before order creation means a
      // failure never leaves an orphaned 'pending' order consuming inventory.
      let paymentResult

      if (paymentMethod === 'apple') {
        paymentResult = await processApplePayment(Math.round(totalAmount * 100), reservation.reservation_id)
      } else if (paymentMethod === 'google') {
        paymentResult = await processGooglePayment(Math.round(totalAmount * 100), reservation.reservation_id)
      } else {
        paymentResult = await processCardPayment(Math.round(totalAmount * 100), reservation.reservation_id)
      }

      if (paymentResult?.errors) {
        const errorMessage = handleSquareError(paymentResult.errors)
        throw new Error(errorMessage)
      }

      // Only now create the order (status 'pending'). The conversion RPC
      // recomputes prices server-side and enforces the inventory cap atomically.
      const convertResponse = await fetch('/.netlify/functions/convert-reservation-to-taste-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reservationId: reservation.reservation_id,
          email: customerEmail.toLowerCase().trim(),
          fullName: customerName.trim(),
          packSize: selectedPack?.size,
          packPrice: selectedPack?.price,
          hasCaviar,
          caviarPrice: hasCaviar ? caviarPrice : 0,
          totalAmount,
          shippingAddress,
          state: shippingAddress.state,
          zip: shippingAddress.zip,
          shippingCost: quote?.shippingCost ?? 0,
          taxAmount: quote?.taxAmount ?? 0,
        }),
      })

      if (!convertResponse.ok) {
        const errorData = await convertResponse.json()
        throw new Error(errorData.error || 'Failed to create order')
      }

      const { order_id, order_number } = await convertResponse.json()
      setOrderId(order_id)
      setOrderNumber(order_number)

      const paymentResponse = await fetch('/.netlify/functions/process-taste-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceId: paymentResult?.token,
          amount: totalAmount,
          orderId: order_id,
          email: customerEmail.toLowerCase().trim(),
          verificationToken: paymentResult?.verificationToken,
          orderDescription: getOrderDescription(),
          customerTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      })

      const paymentData = await paymentResponse.json().catch(() => null)

      // Require an explicit success signal AND a payment id — never advance to
      // the success screen on a non-charge (e.g. a 200 with success:false).
      if (!paymentResponse.ok || !paymentData?.success || !paymentData?.paymentId) {
        throw new Error(paymentData?.error || 'Payment processing failed')
      }

      setStep('success')

      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title="Order Confirmed!"
          message={`Confirmation sent to ${customerEmail}`}
          type="success"
        />
      ))
    } catch (error: any) {
      console.error('Payment processing error:', error)
      const errorMessage = error.message || 'Unable to process payment. Please try again.'
      setPaymentError(errorMessage)

      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          title="Payment Failed"
          message={errorMessage}
          type="error"
        />
      ))
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Labor Day Pre-Order — Taste of Tailtations</title>
        <meta name="description" content="Secure your batch of gourmet deep-fried lobster tails for the ultimate Labor Day cookout. Shipping nationwide for the last great cookout of summer." />
      </Helmet>

      <div
        className="taste-page min-h-screen relative"
        style={{ scrollBehavior: 'smooth', backgroundColor: '#000000', color: '#ffffff' }}
      >
        {/* Subtle Background Pattern */}
        <div
          className="fixed inset-0 opacity-5 pointer-events-none z-0"
          style={{
            backgroundImage: `url(${repeatBg})`,
            backgroundRepeat: 'repeat',
            backgroundSize: '200px',
          }}
        />

        {/* Global Header */}
        <header
          className="py-4 px-4 md:py-8 md:px-8 border-b border-white/10 relative z-10"
          style={{ backgroundColor: 'transparent' }}
        >
          <div className="container mx-auto flex justify-center items-center max-w-7xl">
            {/* Logo - Center. Opens the homepage in a new tab (the only nav now). */}
            <a href="/" target="_blank" rel="noopener noreferrer">
              <img
                src={logoWhite}
                alt="Tailtations Fried Lobster Brand"
                className="w-[180px] md:w-[250px] h-auto"
              />
            </a>
          </div>
        </header>

        {/* STEP: SELECT — Landing Page + Product Selection */}
        {step === 'select' && (
          <>

            {/* Section 1 — Hero */}
            {/* On mobile the lobster sits behind a scrim with centered text;
                on desktop it moves to a dedicated right-hand column so the
                whole tail is visible and bright beside the copy. */}
            <section
              className="relative w-full min-h-screen min-h-[100dvh] flex items-center z-10 overflow-hidden md:bg-cover md:bg-center py-8"
              style={{ backgroundColor: '#0a0a0a' }}
            >
              {/* Mobile background image + scrim (hidden on desktop) */}
              <div
                className="absolute inset-0 md:hidden"
                style={{
                  backgroundImage: `url(${heroImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <div className="absolute inset-0 bg-black/55 md:hidden" />

              <div className="relative z-10 w-full container mx-auto px-6 max-w-7xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 items-center">
                  {/* Text column */}
                  <div className="text-center md:text-left">
                    <h1 className="text-5xl md:text-7xl font-bold !text-white mb-6 font-heading leading-tight">
                      The Undisputed Heavyweight
                      <br />
                      of Labor Day Flavor.
                    </h1>
                    <p className="text-xl md:text-2xl !text-white/60 mb-10 font-body max-w-3xl mx-auto md:mx-0 leading-relaxed">
                      The most exclusive upgrade for the last great cookout of summer. Straight from our kitchen to your door. Limited batches. Once it's gone, it's gone.
                    </p>
                    {!campaignOver && (
                      <div className="mb-10 flex justify-center md:justify-start">
                        <TasteCountdown />
                      </div>
                    )}
                    {campaignOver ? (
                      <div
                        className="inline-block bg-gray-800/80 border border-white/20 !text-white/80 font-bold py-4 px-8 md:py-5 md:px-14 rounded-xl text-xl md:text-2xl font-heading tracking-wider"
                        aria-disabled="true"
                      >
                        {campaignClosed ? 'PRE-ORDER CLOSED' : 'SOLD OUT'}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          document.getElementById('pack-selector-anchor')?.scrollIntoView({ behavior: 'smooth' })
                        }}
                        className="bg-[#C13340] hover:bg-[#962831] !text-white font-bold py-4 px-8 md:py-5 md:px-14 rounded-xl text-xl md:text-2xl font-heading transition-all duration-300 transform hover:scale-105 shadow-[0_0_40px_rgba(193,51,64,0.5)]"
                      >
                        Pre-Order Now &rarr;
                      </button>
                    )}
                  </div>

                  {/* Image column — desktop only; full tail shown, bright */}
                  <div className="hidden md:flex items-center justify-center">
                    <img
                      src={heroImage}
                      alt="Tailtations gourmet deep-fried lobster tail with gold flake"
                      className="w-full max-h-[80vh] object-contain drop-shadow-2xl"
                      style={{ borderRadius: '50px' }}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2 — The Story (3-column process strip) */}
            <section className="relative z-10" style={{ backgroundColor: '#0d0d0d' }}>
              <div className="container mx-auto px-6 py-8 max-w-6xl">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                  {/* The Craft */}
                  <div className="text-center">
                    <div className="rounded-xl overflow-hidden mb-6 aspect-[4/3]">
                      <img src={prepImage} alt="Chef hand-dipping lobster tail in batter" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-2xl font-bold !text-white mb-3 font-heading tracking-wider">THE CRAFT</h3>
                    <p className="!text-white/70 font-body leading-relaxed">
                      Every tail is hand-dipped in our signature batter by our kitchen team before it ever sees heat.
                    </p>
                  </div>

                  {/* The Cook */}
                  <div className="text-center">
                    <div className="rounded-xl overflow-hidden mb-6 aspect-[4/3]">
                      <img src={cookImage} alt="Lobster tail being lifted from fryer" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-2xl font-bold !text-white mb-3 font-heading tracking-wider">THE COOK</h3>
                    <p className="!text-white/70 font-body leading-relaxed">
                      Deep-fried to perfection in a commercial fryer — the same process used at every exclusive event we've ever worked.
                    </p>
                  </div>

                  {/* The Lock */}
                  <div className="text-center">
                    <div className="rounded-xl overflow-hidden mb-6 aspect-[4/3]">
                      <img src={preserveImage} alt="Cooked tails in blast chiller" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-2xl font-bold !text-white mb-3 font-heading tracking-wider">THE LOCK</h3>
                    <p className="!text-white/70 font-body leading-relaxed">
                      Our industrial blast chiller drops each tail to -42&deg;F in record time, preserving the structure and locking in every drop of succulent juice.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2.4 — Video + Celebrity Events */}
            <section className="relative z-10" style={{ backgroundColor: '#0d0d0d' }}>
              <div className="container mx-auto px-6 py-12 max-w-6xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 items-center">
                  {/* Video — YouTube Short (1:1 square) */}
                  <div className="flex justify-center">
                    <div
                      className="w-full max-w-[420px] aspect-square overflow-hidden bg-black"
                      style={{ borderRadius: '24px', transform: 'translateZ(0)' }}
                    >
                      <iframe
                        src="https://www.youtube.com/embed/yozaR5FuU9g?rel=0&modestbranding=1&playsinline=1&controls=0&iv_load_policy=3"
                        title="Tailtations lobster tails"
                        className="w-full h-full"
                        style={{ border: 0 }}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      />
                    </div>
                  </div>

                  {/* Description — desktop only */}
                  <div className="hidden md:block">
                    <h2 className="text-3xl md:text-4xl font-bold !text-white mb-5 font-heading leading-tight">
                      The Flavor Behind the Velvet Rope.
                    </h2>
                    <p className="text-lg !text-white/70 mb-4 font-body leading-relaxed">
                      Since launching a year ago, Tailtations has become the best-kept secret of Los Angeles' most exclusive events — from celebrity galas and private estate dinners to high-end popups where the guest list is a who's who of Hollywood.
                    </p>
                    <p className="text-lg !text-white/70 font-body leading-relaxed">
                      The same tails that disappeared off trays at those parties are now shipping to your door. This isn't a mass-market product. It's a VIP experience, flash-frozen and ready to fry.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2.5 — Freezer to Table (ease-of-use) */}
            <FreezerToTable />

            {/* Section 2.6 — Shipping region grid + ZIP estimator (set
                expectations BEFORE the pack/price section to avoid sticker shock) */}
            {!campaignOver && <ShippingEstimator />}

            {/* Section 3 — The Offer + Product Selection */}
            <section id="product-selection" className="relative z-10" style={{ backgroundColor: '#111111' }}>
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: `url(${repeatBg})`,
                  backgroundRepeat: 'repeat',
                  opacity: 0.08,
                  mixBlendMode: 'multiply'
                }}
              />
              <div className="container mx-auto px-6 py-8 max-w-6xl relative z-10">
                {/* Offer Header with Image */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16 items-center">
                  <div>
                    <h2 className="text-4xl md:text-5xl font-bold !text-white mb-6 font-heading leading-tight">
                      The Taste of Tailtations. Delivered.
                    </h2>
                    <p className="text-lg !text-white/70 mb-4 font-body leading-relaxed">
                      Zero compromises. 100% authentic. We're the name behind the most exclusive events in Los Angeles — the flavor that people don't forget. For years, Tailtations has been the best-kept secret of the culinary elite.
                    </p>
                    <p className="text-lg !text-white/70 font-body leading-relaxed">
                      Now, for the first time ever, we are unlocking the vault. Unapologetic flavor. Limited batches. All orders ship via UPS 2-Day no later than <strong>{CAMPAIGN.shipDateDisplay}</strong> — arriving just in time for your Labor Day cookout.
                    </p>
                    <ShippingExpectationNote className="mt-6" />
                  </div>
                  <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                    <img src={offerImage} alt="Tailtations branded boxes with lobster tails and caviar" className="w-full h-auto" />
                  </div>
                </div>

                <div id="pack-selector-anchor" />

                {campaignOver ? (
                  /* Campaign-wide Sold Out / Closed — no purchase path */
                  <div className="max-w-2xl mx-auto text-center bg-white/5 border border-white/10 rounded-2xl p-10 md:p-14">
                    <div className="inline-block bg-gray-800/80 border border-white/20 !text-white font-bold py-2 px-6 rounded-full text-sm tracking-widest mb-6">
                      {campaignClosed ? 'PRE-ORDER CLOSED' : 'SOLD OUT'}
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold !text-white mb-4 font-heading">
                      {campaignClosed ? 'This Batch Is Closed' : 'This Batch Is Sold Out'}
                    </h3>
                    <p className="text-lg !text-white/60 mb-8 leading-relaxed">
                      {campaignClosed
                        ? 'The Labor Day pre-order window has ended. Drop your email and we\'ll notify you first when the next batch drops.'
                        : 'Every last tail in this limited run has been claimed. Thank you — once it\'s gone, it\'s gone. Drop your email and we\'ll notify you first when the next batch drops.'}
                    </p>
                    <WaitlistForm />
                  </div>
                ) : (
                  <>
                    {/* Pack Selector */}
                    <PackSelector />

                    {/* Custom / bulk order CTA — placed right under the pack grid,
                        the moment a buyer realizes 8 is the largest pack. Custom
                        orders are quote-and-approve only, so this routes to email
                        rather than the standard checkout. */}
                    <div className="mb-8 max-w-3xl mx-auto bg-white/5 border border-[#2563EB]/40 rounded-2xl p-6 md:p-7 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-center sm:text-left">
                      {/* Cute waving American flag — uses page tokens (blue #2563EB,
                          red #C13340, white). Decorative, so aria-hidden. */}
                      <svg
                        viewBox="0 0 64 56"
                        width="64"
                        height="56"
                        className="w-16 h-14 md:w-20 md:h-16 flex-shrink-0 drop-shadow-lg"
                        aria-hidden="true"
                        focusable="false"
                      >
                        {/* Flagpole */}
                        <rect x="5" y="2" width="3" height="52" rx="1.5" fill="#9ca3af" />
                        <circle cx="6.5" cy="3" r="2.5" fill="#2563EB" />
                        {/* Waving flag body, clipped so stripes follow the wave */}
                        <defs>
                          <clipPath id="taste-flag-wave">
                            <path d="M8 6
                              C 22 2, 36 12, 50 8
                              C 56 6.5, 60 9, 62 8
                              L 62 34
                              C 60 35, 56 32.5, 50 34
                              C 36 38, 22 28, 8 32 Z" />
                          </clipPath>
                        </defs>
                        <g clipPath="url(#taste-flag-wave)">
                          {/* 7 red stripes / white gaps across the flag's vertical span (6 → 34) */}
                          <rect x="6" y="6" width="58" height="28" fill="#ffffff" />
                          <rect x="6" y="6" width="58" height="4" fill="#C13340" />
                          <rect x="6" y="14" width="58" height="4" fill="#C13340" />
                          <rect x="6" y="22" width="58" height="4" fill="#C13340" />
                          <rect x="6" y="30" width="58" height="4" fill="#C13340" />
                          {/* Blue canton */}
                          <rect x="6" y="6" width="26" height="15" fill="#2563EB" />
                          {/* A few cute stars */}
                          <g fill="#ffffff">
                            <circle cx="12" cy="10" r="1.1" />
                            <circle cx="19" cy="10" r="1.1" />
                            <circle cx="26" cy="10" r="1.1" />
                            <circle cx="15.5" cy="15" r="1.1" />
                            <circle cx="22.5" cy="15" r="1.1" />
                          </g>
                        </g>
                      </svg>
                      <div className="flex-1">
                        <h3 className="text-lg md:text-xl font-bold !text-white font-heading mb-1">
                          Throwing a big Labor Day bash?
                        </h3>
                        <p className="text-sm md:text-base !text-white/60 font-body leading-relaxed">
                          Feeding a whole cookout takes more than 8. Tell us your headcount and we'll put together a custom large-batch order — quoted and shipped in time to be the star of your celebration.
                        </p>
                      </div>
                      <a
                        href={`mailto:contact@tailtations.com?subject=${encodeURIComponent('Custom Labor Day Order — Taste of Tailtations')}&body=${encodeURIComponent("Hi Tailtations team,\n\nI'm planning a Labor Day gathering and need more than 8 tails. Could you send a quote?\n\nNumber of tails:\nCaviar add-on? (yes/no):\nNeeded-by date:\nShipping ZIP:\n\nThanks!")}`}
                        className="w-full sm:w-auto flex-shrink-0 inline-block bg-[#2563EB] hover:bg-[#1D4ED8] !text-white font-bold py-3 px-6 rounded-xl text-base font-heading transition-colors duration-300 whitespace-nowrap"
                      >
                        Request a Custom Quote
                      </a>
                    </div>

                    {/* Caviar Upsell */}
                    <CaviarUpsell />

                    {/* Order Summary */}
                    {selectedPack && (
                      <div className="max-w-md mx-auto mb-8">
                        <ShippingExpectationNote className="mb-4" />
                        <OrderSummary />
                      </div>
                    )}

                  </>
                )}
              </div>
            </section>

            {/* Section 4 — FAQ */}
            {!campaignOver && <TasteFaq />}

            {/* Spacer at page end so the fixed buy bar never covers the footer.
                Placed here (not between sections) to keep inter-section gaps even. */}
            {selectedPack && <div className="h-24"></div>}

            {/* Persistent sticky buy bar (R8), all breakpoints. Rendered as a
                direct child of the page root — NOT nested inside a `z-10`
                section — so its z-50 isn't trapped in a lower stacking context
                and reliably sits above every section (incl. the FAQ). */}
            {selectedPack && !campaignOver && (
              <div className="fixed bottom-0 left-0 right-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-white/10 shadow-lg z-50" style={{ backgroundColor: '#111111' }}>
                <div className="container mx-auto max-w-7xl flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  {/* Selection summary — desktop only */}
                  <div className="hidden md:block">
                    <span className="font-bold font-heading text-lg !text-white">
                      {selectedPack.size}-Pack{hasCaviar && ' + Caviar'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleContinueToDetails}
                    className="w-full md:w-auto bg-[#962831] hover:bg-[#7a2029] !text-white font-bold py-4 md:py-4 px-8 md:px-14 rounded-xl text-lg md:text-xl shadow-lg md:transform md:hover:scale-105 transition-all duration-300"
                  >
                    Secure your {selectedPack.size}-Pack &rarr;
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* STEP: DETAILS — Customer Info + Shipping */}
        {step === 'details' && (
          <main className="w-full relative z-10" style={{ backgroundColor: '#111111' }}>
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: `url(${repeatBg})`,
                backgroundRepeat: 'repeat',
                opacity: 0.08,
                mixBlendMode: 'multiply'
              }}
            />
            <div className="container mx-auto px-4 py-16 max-w-2xl relative z-10">
              <h2 className="text-4xl font-bold !text-white mb-10 text-center font-heading">
                Your Information
              </h2>

              {/* Summary Card */}
              <div className="bg-gradient-to-br from-[#962831] to-[#6B2A2A] rounded-2xl shadow-2xl p-6 md:p-8 mb-10">
                <div className="text-center">
                  <div className="text-lg mb-2 !text-white">Your Order</div>
                  <div className="text-3xl md:text-4xl font-bold mb-3 !text-white">
                    {selectedPack?.label} Pack
                    {hasCaviar && ' + Caviar'}
                  </div>
                  <div className="text-4xl md:text-5xl font-bold mb-3 !text-white">${totalAmount.toFixed(2)}</div>
                </div>
              </div>

              {/* Email Input */}
              <div id="email-input-section" className="bg-white/5 border border-white/10 rounded-2xl shadow-xl p-5 md:p-8 mb-6">
                <label className="block text-sm font-semibold !text-white/70 mb-2">
                  Email Address <span className="!text-red-500">*</span>
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={customerEmail}
                  onChange={(e) => {
                    setCustomerEmail(e.target.value)
                    setEmailError(null)
                  }}
                  placeholder="your@email.com"
                  className={`w-full px-4 py-4 border-2 rounded-xl focus:outline-none bg-white/10 !text-white text-lg placeholder-white/40 ${
                    emailError
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-white/20 focus:border-[#962831]'
                  }`}
                />
                {emailError && <p className="mt-2 text-sm text-red-500">{emailError}</p>}
                <p className="mt-2 text-sm !text-white/40">Required for your order confirmation</p>
              </div>

              {/* Full Name */}
              <div className="bg-white/5 border border-white/10 rounded-2xl shadow-xl p-5 md:p-8 mb-6">
                <label className="block text-sm font-semibold !text-white/70 mb-2">
                  Full Name <span className="!text-red-500">*</span>
                </label>
                <input
                  type="text"
                  autoComplete="name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="First and Last Name"
                  className="w-full px-4 py-4 border-2 border-white/20 rounded-xl focus:border-[#962831] focus:outline-none bg-white/10 !text-white text-lg placeholder-white/40"
                />
              </div>

              {/* Shipping Address */}
              <div className="bg-white/5 border border-white/10 rounded-2xl shadow-xl p-5 md:p-8 mb-6">
                <h3 className="text-lg font-bold !text-white mb-4">Shipping Address</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold !text-white/70 mb-1">
                      Street Address <span className="!text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      autoComplete="address-line1"
                      value={shippingAddress.street}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, street: e.target.value })}
                      placeholder="123 Main St"
                      className={`w-full px-4 py-3 text-base border-2 rounded-xl focus:outline-none bg-white/10 !text-white placeholder-white/40 ${
                        addressErrors.street ? 'border-red-500' : 'border-white/20 focus:border-[#962831]'
                      }`}
                    />
                    {addressErrors.street && <p className="mt-1 text-sm text-red-500">{addressErrors.street}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold !text-white/70 mb-1">
                      Apt / Suite / Unit
                    </label>
                    <input
                      type="text"
                      autoComplete="address-line2"
                      value={shippingAddress.apt}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, apt: e.target.value })}
                      placeholder="Apt 4B"
                      className="w-full px-4 py-3 text-base border-2 border-white/20 rounded-xl focus:border-[#962831] focus:outline-none bg-white/10 !text-white placeholder-white/40"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold !text-white/70 mb-1">
                        City <span className="!text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        autoComplete="address-level2"
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                        placeholder="Los Angeles"
                        className={`w-full px-4 py-3 text-base border-2 rounded-xl focus:outline-none bg-white/10 !text-white placeholder-white/40 ${
                          addressErrors.city ? 'border-red-500' : 'border-white/20 focus:border-[#962831]'
                        }`}
                      />
                      {addressErrors.city && <p className="mt-1 text-sm text-red-500">{addressErrors.city}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold !text-white/70 mb-1">
                        State <span className="!text-red-500">*</span>
                      </label>
                      <select
                        value={shippingAddress.state}
                        autoComplete="address-level1"
                        onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                        className={`w-full px-4 py-3 text-base border-2 rounded-xl focus:outline-none bg-white/10 !text-white placeholder-white/40 ${
                          addressErrors.state ? 'border-red-500' : 'border-white/20 focus:border-[#962831]'
                        }`}
                      >
                        <option value="">Select</option>
                        {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {addressErrors.state && <p className="mt-1 text-sm text-red-500">{addressErrors.state}</p>}
                      {(shippingAddress.state === 'AK' || shippingAddress.state === 'HI') && (
                        <p className="mt-1 text-sm text-red-500">{SHIP_RESTRICTED_MESSAGE}</p>
                      )}
                    </div>
                  </div>

                  <div className="w-1/2">
                    <label className="block text-sm font-semibold !text-white/70 mb-1">
                      ZIP Code <span className="!text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      value={shippingAddress.zip}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, zip: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                      onBlur={() => fetchQuote()}
                      placeholder="90001"
                      maxLength={5}
                      className={`w-full px-4 py-3 text-base border-2 rounded-xl focus:outline-none bg-white/10 !text-white placeholder-white/40 ${
                        addressErrors.zip ? 'border-red-500' : 'border-white/20 focus:border-[#962831]'
                      }`}
                    />
                    {addressErrors.zip && <p className="mt-1 text-sm text-red-500">{addressErrors.zip}</p>}
                    {quoteLoading && !addressErrors.zip && (
                      <p className="mt-1 text-sm !text-white/50">Updating shipping…</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Allergen Notice */}
              <div className="bg-red-950/30 border border-red-800/50 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 !text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm !text-red-300">
                    <p className="font-semibold mb-1 !text-inherit">Allergen Notice</p>
                    <p className="!text-inherit">Our products contain <strong>shellfish (lobster)</strong> and may contain or come into contact with wheat, eggs, milk, soy, and tree nuts. If you have a food allergy, please contact us before ordering.</p>
                  </div>
                </div>
              </div>

              {/* Shipping Restrictions Notice */}
              <div className="bg-blue-950/30 border border-blue-800/50 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <Package className="w-5 h-5 !text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm !text-blue-300">
                    <p className="font-semibold mb-1 !text-inherit">Shipping Info</p>
                    <p className="!text-inherit">We ship to all 50 U.S. states + D.C. No P.O. Boxes. Products arrive flash-frozen in insulated packaging with dry ice. Someone must be available to receive the package on delivery day.</p>
                  </div>
                </div>
              </div>

              {/* Pre-Order Transparency Notice */}
              <div className="bg-amber-950/30 border border-amber-800/50 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 !text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm !text-amber-300">
                    <p className="font-semibold mb-1 !text-inherit">Pre-Order Notice</p>
                    <p className="!text-inherit">Your payment will be charged in full today to secure your allocation. All orders ship via UPS 2-Day no later than <strong>{CAMPAIGN.shipDateDisplay}</strong> for delivery before Labor Day weekend. You may cancel within 48 hours for a full refund.</p>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="mb-10">
                <OrderSummary />
                {quote?.shippingEstimated && (
                  <p className="mt-3 text-xs !text-white/50 text-center">
                    Shipping estimated for your area.
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="fixed bottom-0 left-0 right-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-white/10 shadow-lg md:relative md:p-0 md:pb-0 md:bg-transparent md:border-0 md:shadow-none z-50" style={{ backgroundColor: '#111111' }}>
                <div className="flex space-x-4 max-w-2xl mx-auto">
                  <button
                    type="button"
                    onClick={() => setStep('select')}
                    className="flex-1 bg-white/10 hover:bg-white/20 !text-white font-bold py-4 px-6 rounded-xl transition-colors text-lg"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleContinueToPayment}
                    disabled={isCreatingReservation}
                    className="flex-1 bg-[#962831] hover:bg-[#6B2A2A] !text-white font-bold py-4 px-6 rounded-xl transition-colors text-lg disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isCreatingReservation ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 !text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Continue to Payment \u2192'
                    )}
                  </button>
                </div>
              </div>
              {/* Spacer for fixed button on mobile */}
              <div className="h-24 md:hidden"></div>
            </div>
          </main>
        )}

        {/* STEP: PAYMENT */}
        {step === 'payment' && reservation && (
          <main className="w-full relative z-10" style={{ backgroundColor: '#111111' }}>
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: `url(${repeatBg})`,
                backgroundRepeat: 'repeat',
                opacity: 0.08,
                mixBlendMode: 'multiply'
              }}
            />
            <div className="container mx-auto px-4 py-16 max-w-2xl relative z-10">
              <h2 className="text-4xl font-bold !text-white mb-10 text-center font-heading">
                Complete Your Order
              </h2>

              <ReservationTimer expiresAt={reservation.expires_at} onExpire={handleReservationExpire} />

              {/* Payment Summary — itemized so the all-in total is explained */}
              <div className="bg-gradient-to-br from-[#962831] to-[#6B2A2A] rounded-2xl shadow-2xl p-6 md:p-8 mb-10">
                <div className="text-center mb-5">
                  <div className="text-lg mb-1 !text-white/80">Total Amount</div>
                  <div className="text-4xl md:text-5xl font-bold !text-white">${totalAmount.toFixed(2)}</div>
                </div>

                {/* Itemized breakdown */}
                <div className="bg-black/15 rounded-xl p-4 md:p-5 space-y-2 text-sm md:text-base">
                  <div className="flex justify-between">
                    <span className="!text-white/80">
                      {selectedPack?.label} Pack{hasCaviar && ' + Caviar'}
                    </span>
                    <span className="font-semibold !text-white">
                      ${(quote ? quote.subtotal : ((selectedPack?.price || 0) + (hasCaviar ? caviarPrice : 0))).toFixed(2)}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="!text-white/80">
                      Shipping{shippingAddress.zip ? ` to ${shippingAddress.zip}` : ''}
                      <span className="block text-xs !text-white/55">UPS 2-Day</span>
                    </span>
                    <span className="font-semibold !text-white">
                      {quote ? `$${quote.shippingCost.toFixed(2)}` : '—'}
                    </span>
                  </div>

                  {quote && quote.taxAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="!text-white/80">Sales Tax</span>
                      <span className="font-semibold !text-white">${quote.taxAmount.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="border-t border-white/20 pt-2 mt-1 flex justify-between">
                    <span className="font-bold !text-white">Total</span>
                    <span className="font-bold !text-white">${totalAmount.toFixed(2)}</span>
                  </div>

                  {quote?.shippingEstimated && (
                    <p className="text-xs !text-white/55 pt-1">Shipping estimated for your area.</p>
                  )}
                </div>

                {customerEmail && (
                  <div className="mt-4 text-center text-sm !text-white/80">Confirmation will be sent to: {customerEmail}</div>
                )}
              </div>

              {/* Payment Form */}
              <div className="bg-white/5 border border-white/10 rounded-2xl shadow-xl p-5 md:p-8">
                <TastePaymentForm
                  containerId="taste-card-container"
                  onPaymentComplete={() => handleProcessPayment('card')}
                  onApplePayComplete={() => handleProcessPayment('apple')}
                  onGooglePayComplete={() => handleProcessPayment('google')}
                  isProcessing={isProcessing}
                  buttonText={`Pay $${totalAmount.toFixed(2)}`}
                />
              </div>
            </div>
          </main>
        )}

        {/* STEP: SUCCESS */}
        {step === 'success' && orderNumber && (
          <main className="w-full relative z-10" style={{ backgroundColor: '#111111' }}>
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: `url(${repeatBg})`,
                backgroundRepeat: 'repeat',
                opacity: 0.08,
                mixBlendMode: 'multiply'
              }}
            />
            <div className="container mx-auto px-4 py-16 max-w-[800px] relative z-10">
              {/* Main Confirmation Card */}
              <div className="bg-gradient-to-br from-[#962831] to-[#6B2A2A] rounded-2xl shadow-2xl p-6 md:p-12 mb-8 text-center">
                <div className="flex items-center justify-center mb-6">
                  <Package className="w-16 h-16 !text-white" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold !text-white mb-6 font-heading">
                  Your Order Is In.
                  <br />
                  Welcome to the Inner Circle.
                </h2>
                <p className="text-lg md:text-xl !text-white leading-relaxed">
                  Thank you for your pre-order. You're now part of an exclusive group of people who will receive Tailtations at home for the very first time.
                </p>
              </div>

              {/* Order Details Card */}
              <div className="bg-white/5 border-2 border-[#2563EB] rounded-xl shadow-lg p-5 md:p-8 mb-8">
                <div className="text-center mb-6">
                  <div className="text-xs font-semibold tracking-wide uppercase !text-white/50 mb-2">
                    Order Number
                  </div>
                  <div className="text-2xl md:text-3xl font-bold !text-white">
                    {orderNumber}
                  </div>
                </div>

                <div className="border-t border-white/10 my-6"></div>

                {/* Order Summary */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="!text-white/60">{selectedPack?.label} Pack</span>
                    <span className="font-semibold !text-white">${selectedPack?.price.toFixed(2)}</span>
                  </div>
                  {hasCaviar && (
                    <div className="flex justify-between">
                      <span className="!text-white/60">Caviar Add-On</span>
                      <span className="font-semibold !text-white">${caviarPrice.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 pt-3">
                    <div className="flex justify-between text-xl font-bold">
                      <span className="!text-white">Total Charged</span>
                      <span className="!text-[#962831]">${totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="text-sm font-semibold !text-white/50 mb-2">Ships To</div>
                  <div className="!text-white">
                    <div className="!text-white">{customerName}</div>
                    <div className="!text-white">{shippingAddress.street}{shippingAddress.apt ? `, ${shippingAddress.apt}` : ''}</div>
                    <div className="!text-white">{shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}</div>
                  </div>
                </div>

                <div className="border-t border-white/10 my-6"></div>

                <p className="text-sm !text-white/70 text-center mb-4">
                  Your order is confirmed and will ship via UPS 2-Day no later than <span className="font-semibold">{CAMPAIGN.shipDateDisplay}</span> for delivery before Labor Day weekend. We'll send tracking info to <span className="font-semibold">{customerEmail}</span> the moment it leaves our kitchen.
                </p>

                <div className="bg-blue-950/30 border border-blue-800/50 rounded-lg p-4 text-sm !text-blue-300">
                  <p className="font-semibold mb-1 !text-inherit">Cancellation Policy</p>
                  <p className="!text-inherit">You may cancel within 48 hours of purchase for a full refund by emailing <a href="mailto:contact@tailtations.com" className="underline font-medium">contact@tailtations.com</a>. After 48 hours, all sales are final.</p>
                </div>
              </div>

              {/* Social Share */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 md:p-8 mb-8 text-center">
                <p className="text-xl font-bold !text-white mb-4 font-heading">
                  Tell someone. They'll be jealous.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                  <a
                    href="https://www.instagram.com/tailtations/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 !text-white font-semibold py-3 px-6 rounded-lg hover:opacity-90 transition-opacity"
                  >
                    <Share2 className="w-4 h-4" />
                    Share on Instagram
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=I%20just%20pre-ordered%20Tailtations%20lobster%20tails%20shipped%20to%20my%20door!%20🦞&url=https://tailtations.com/taste`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-black !text-white font-semibold py-3 px-6 rounded-lg hover:opacity-90 transition-opacity"
                  >
                    <Share2 className="w-4 h-4" />
                    Share on X
                  </a>
                </div>
              </div>

              {/* Return to Home CTA */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="bg-[#962831] hover:bg-[#7D4545] !text-white font-semibold py-4 px-8 rounded-lg transition-all duration-200 ease-in-out transform hover:scale-105 text-base"
                >
                  Return to Home
                </button>
              </div>
            </div>
          </main>
        )}

        {/* Global Footer */}
        <Footer />
      </div>
    </>
  )
}

export default TastePage
